import { isValidElement, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Download, Rows3, Rows4, Search, SearchX } from "lucide-react";
import { EmptyState } from "./display";
import { Button } from "./Button";
import { cx } from "../lib/cx";
import { formatMoney, formatMoneyCsv, formatInt } from "../lib/money";
import { formatDate, formatDateTime } from "../lib/dates";
import { formatDoc } from "../lib/documents";
import { downloadText, toCsv, type CsvValue } from "../lib/csv";

export type ColumnKind = "text" | "money" | "date" | "datetime" | "number" | "doc";

export interface Column<Row> {
  /** Identificador; também é o campo de ordenação quando `sortable` (ou `sortKey`). */
  key: string;
  header: ReactNode;
  /** Cabeçalho em texto puro para o CSV quando `header` não é string. */
  csvHeader?: string;
  cell?: (row: Row) => ReactNode;
  kind?: ColumnKind;
  sortable?: boolean;
  sortKey?: string;
  align?: "left" | "right" | "center";
  /** Valor no CSV; `false` tira a coluna do CSV. */
  csv?: ((row: Row) => CsvValue) | false;
  width?: string;
}

export type SortDir = "asc" | "desc";

export interface TableQuery {
  page: number;
  pageSize: number;
  sort: { key: string; dir: SortDir } | null;
  search: string;
  filters: Record<string, string>;
}

export interface TablePage<Row> {
  rows: Row[];
  /** Total no servidor (Content-Range); null quando desconhecido. */
  total: number | null;
}

/** Fonte de dados paginada no servidor. `@vixio/data` fornece `postgrestSource`. */
export interface TableSource<Row> {
  key: readonly unknown[];
  fetch: (q: TableQuery) => Promise<TablePage<Row>>;
}

export interface FilterDef {
  key: string;
  label: string;
  options: Array<{ value: string; label: string }>;
  /** Rótulo da opção "todos" (valor vazio). */
  all?: string;
}

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  /** Fonte no servidor... */
  source?: TableSource<Row>;
  /** ...ou linhas já carregadas (ordenação, busca e paginação locais). */
  data?: Row[];
  /** Campos usados na busca local (modo `data`). */
  searchKeys?: Array<keyof Row & string>;
  rowKey: (row: Row) => string;
  caption: string;
  onRowClick?: (row: Row) => void;
  searchPlaceholder?: string;
  filters?: FilterDef[];
  initialFilters?: Record<string, string>;
  initialSort?: { key: string; dir: SortDir };
  pageSize?: number;
  exportName?: string;
  toolbar?: ReactNode;
  empty?: ReactNode;
  footer?: (rows: Row[]) => ReactNode;
  loading?: boolean;
  error?: string | null;
  /** Sem barra de busca/filtros. */
  bare?: boolean;
}

const PAGE_SIZES = [25, 50, 100];
const DENSITY_KEY = "vixio.table-density";
type Density = "comfortable" | "compact";

function readDensity(): Density {
  try {
    return localStorage.getItem(DENSITY_KEY) === "compact" ? "compact" : "comfortable";
  } catch {
    return "comfortable";
  }
}

/** Texto do cabeçalho para o rótulo da célula no modo cartão (celular). */
function headerText<Row>(col: Column<Row>): string {
  if (col.csvHeader) return col.csvHeader;
  if (typeof col.header === "string") return col.header;
  if (isValidElement(col.header)) {
    const c = (col.header.props as { children?: unknown }).children;
    if (typeof c === "string") return c;
  }
  return "";
}

/**
 * Cabeçalho fixo no topo da página: só quando a tabela cabe na largura (sem rolagem horizontal, o
 * overflow pode ficar visível e o position: sticky do <thead> passa a valer em relação à página).
 */
function useFits(): [(el: HTMLDivElement | null) => void, boolean] {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [fits, setFits] = useState(false);
  useEffect(() => {
    // Dentro de janela (diálogo, painel lateral) quem rola é a janela: o cabeçalho fixo na página não faz sentido.
    if (!el || typeof ResizeObserver === "undefined" || el.closest(".vx-dialog, .vx-sheet, .vx-cmd")) return;
    const check = () => {
      const t = el.firstElementChild as HTMLElement | null;
      setFits(Boolean(t) && t!.scrollWidth <= el.clientWidth + 1 && window.innerWidth > 640);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, fits];
}

function defaultCell<Row>(col: Column<Row>, row: Row): ReactNode {
  const v = (row as Record<string, unknown>)[col.key];
  if (v === null || v === undefined || v === "") return <span className="muted">-</span>;
  switch (col.kind) {
    case "money":
      return <span className="num">{formatMoney(v as number)}</span>;
    case "date":
      return <span className="num">{formatDate(String(v))}</span>;
    case "datetime":
      return <span className="num">{formatDateTime(String(v))}</span>;
    case "number":
      return <span className="num">{formatInt(v as number)}</span>;
    case "doc":
      return <span className="mono">{formatDoc(String(v))}</span>;
    default:
      return String(v);
  }
}

function csvValue<Row>(col: Column<Row>, row: Row): CsvValue {
  if (typeof col.csv === "function") return col.csv(row);
  const v = (row as Record<string, unknown>)[col.key];
  if (v === null || v === undefined) return "";
  switch (col.kind) {
    case "money":
      return formatMoneyCsv(v as number);
    case "date":
      return formatDate(String(v));
    case "datetime":
      return formatDateTime(String(v));
    case "doc":
      return formatDoc(String(v));
    default:
      return typeof v === "object" ? JSON.stringify(v) : (v as CsvValue);
  }
}

function alignOf<Row>(col: Column<Row>): "left" | "right" | "center" {
  return col.align ?? (col.kind === "money" || col.kind === "number" ? "right" : "left");
}

export function localSource<Row>(rows: Row[], searchKeys: string[] = []): TableSource<Row> {
  return {
    key: ["local", rows],
    async fetch(q) {
      let out = rows;
      const s = q.search.trim().toLowerCase();
      if (s) {
        out = out.filter((r) =>
          searchKeys.some((k) => String((r as Record<string, unknown>)[k] ?? "").toLowerCase().includes(s)),
        );
      }
      for (const [k, v] of Object.entries(q.filters)) {
        if (v !== "") out = out.filter((r) => String((r as Record<string, unknown>)[k] ?? "") === v);
      }
      if (q.sort) {
        const { key, dir } = q.sort;
        out = [...out].sort((a, b) => {
          const va = (a as Record<string, unknown>)[key];
          const vb = (b as Record<string, unknown>)[key];
          if (va === vb) return 0;
          if (va === null || va === undefined) return 1;
          if (vb === null || vb === undefined) return -1;
          const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb), "pt-BR");
          return dir === "asc" ? cmp : -cmp;
        });
      }
      const start = q.page * q.pageSize;
      return { rows: out.slice(start, start + q.pageSize), total: out.length };
    },
  };
}

export function DataTable<Row>(props: DataTableProps<Row>) {
  const {
    columns, source: remote, data, searchKeys, rowKey, caption, onRowClick, searchPlaceholder = "Buscar", filters = [],
    initialFilters, initialSort, pageSize: initialPageSize = 25, exportName, toolbar, empty, footer, bare,
  } = props;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sort, setSort] = useState<TableQuery["sort"]>(initialSort ?? null);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>(initialFilters ?? {});
  const [exporting, setExporting] = useState(false);
  const [density, setDensity] = useState<Density>(() => (typeof window === "undefined" ? "comfortable" : readDensity()));
  const [wrapRef, fits] = useFits();
  const topRef = useRef<HTMLDivElement>(null);

  function toggleDensity() {
    const next: Density = density === "compact" ? "comfortable" : "compact";
    setDensity(next);
    try {
      localStorage.setItem(DENSITY_KEY, next);
    } catch {
      /* navegação privada */
    }
  }

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const source = useMemo(() => remote ?? localSource(data ?? [], (searchKeys as string[]) ?? []), [remote, data, searchKeys]);
  const query: TableQuery = { page, pageSize, sort, search, filters: filterValues };

  const q = useQuery({
    queryKey: [...source.key, "table", query],
    queryFn: () => source.fetch(query),
    placeholderData: keepPreviousData,
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? null;
  const from = rows.length === 0 ? 0 : page * pageSize + 1;
  const to = page * pageSize + rows.length;
  const hasNext = total !== null ? to < total : rows.length === pageSize;
  const pages = total !== null ? Math.max(1, Math.ceil(total / pageSize)) : null;
  const filtered = Boolean(search || Object.values(filterValues).some(Boolean));
  const errorText = props.error ?? (q.error instanceof Error ? q.error.message : q.error ? String(q.error) : null);
  const loading = props.loading || (q.isLoading && !q.data);

  function toggleSort(col: Column<Row>) {
    const key = col.sortKey ?? col.key;
    setPage(0);
    setSort((s) => (s?.key !== key ? { key, dir: "asc" } : s.dir === "asc" ? { key, dir: "desc" } : null));
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const all = await source.fetch({ ...query, page: 0, pageSize: 5000 });
      const cols = columns.filter((c) => c.csv !== false);
      const csv = toCsv(
        all.rows,
        cols.map((c) => ({ header: c.csvHeader ?? (typeof c.header === "string" ? c.header : c.key), value: (r: Row) => csvValue(c, r) })),
      );
      downloadText(`${exportName ?? "exportacao"}.csv`, csv);
    } finally {
      setExporting(false);
    }
  }

  const showToolbar = !bare && (searchKeys?.length || remote || filters.length > 0 || toolbar || exportName);

  function goPage(p: number) {
    setPage(p);
    // Volta ao topo da tabela ao trocar de página (sem pular a página inteira).
    const top = topRef.current?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) topRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }

  return (
    <div className="vx-panel vx-dt" ref={topRef}>
      {showToolbar ? (
        <div className="vx-table-toolbar" role="search">
          {(remote || (searchKeys && searchKeys.length > 0)) && (
            <div className="vx-table-toolbar__search">
              <Search aria-hidden="true" />
              <input
                className="vx-input"
                type="search"
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          )}
          {filters.map((f) => (
            <select
              key={f.key}
              className="vx-select"
              aria-label={f.label}
              value={filterValues[f.key] ?? ""}
              onChange={(e) => {
                setPage(0);
                setFilterValues((v) => ({ ...v, [f.key]: e.target.value }));
              }}
            >
              <option value="">{f.all ?? `${f.label}: todos`}</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ))}
          <span className="vx-spacer" />
          {toolbar}
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            label={density === "compact" ? "Linhas confortáveis" : "Linhas compactas"}
            icon={density === "compact" ? <Rows3 /> : <Rows4 />}
            aria-pressed={density === "compact"}
            onClick={toggleDensity}
            className="vx-dt__density"
          />
          {exportName ? (
            <Button size="sm" icon={<Download />} onClick={exportCsv} loading={exporting} disabled={rows.length === 0}>
              CSV
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className={cx("vx-table-wrap", fits && "is-fit")} ref={wrapRef}>
        <table className={cx("vx-table", "vx-table--cards", density === "compact" && "vx-table--compact")} aria-busy={q.isFetching || undefined}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              {columns.map((c) => {
                const key = c.sortKey ?? c.key;
                const active = sort?.key === key;
                const align = alignOf(c);
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={cx(align === "right" && "is-right", align === "center" && "is-center")}
                    style={c.width ? { width: c.width } : undefined}
                    aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : c.sortable ? "none" : undefined}
                  >
                    {c.sortable ? (
                      <button type="button" className="vx-table__sort" onClick={() => toggleSort(c)} aria-pressed={active}>
                        {c.header}
                        {active ? sort!.dir === "asc" ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" /> : <ChevronsUpDown aria-hidden="true" />}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }, (_, i) => (
                <tr key={`sk-${i}`} className="vx-table__skel" aria-hidden={i > 0 || undefined}>
                  {columns.map((c, ci) => (
                    <td key={c.key}>
                      {i === 0 && ci === 0 ? <span className="sr-only">Carregando</span> : null}
                      <span className="vx-skel" style={{ width: `${[72, 56, 64, 48, 60][(i + ci) % 5]}%` }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : errorText ? (
              <tr className="vx-table__state">
                <td colSpan={columns.length} className="vx-table-status vx-table-status--error" role="alert">
                  <AlertTriangle aria-hidden="true" />
                  <span>{errorText}</span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr className="vx-table__state">
                <td colSpan={columns.length} className="vx-table-status vx-table-status--empty">
                  {empty === undefined || typeof empty === "string" ? (
                    <EmptyState compact icon={filtered ? SearchX : undefined} title={empty ?? (filtered ? "Nenhum registro com esses filtros." : "Nenhum registro.")} />
                  ) : (
                    empty
                  )}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={rowKey(r)} className={cx(onRowClick && "is-clickable")} onClick={onRowClick ? () => onRowClick(r) : undefined}>
                  {columns.map((c) => {
                    const align = alignOf(c);
                    return (
                      <td key={c.key} className={cx(align === "right" && "is-right", align === "center" && "is-center")} data-label={headerText(c) || undefined}>
                        {c.cell ? c.cell(r) : defaultCell(c, r)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
          {footer && rows.length > 0 ? <tfoot>{footer(rows)}</tfoot> : null}
        </table>
      </div>
      {(total ?? rows.length) > Math.min(pageSize, PAGE_SIZES[0]!) || page > 0 ? (
        <div className="vx-pager">
          <span className="num" aria-live="polite">
            {from} a {to}
            {total !== null ? ` de ${formatInt(total)}` : ""}
          </span>
          {pages !== null && pages > 1 ? (
            <span className="num vx-pager__pages">
              página {page + 1} de {formatInt(pages)}
            </span>
          ) : null}
          <span className="vx-spacer" />
          <label className="sr-only" htmlFor="vx-page-size">
            Linhas por página
          </label>
          <select
            id="vx-page-size"
            className="vx-select"
            value={pageSize}
            onChange={(e) => {
              setPage(0);
              setPageSize(Number(e.target.value));
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n} por página
              </option>
            ))}
          </select>
          <Button size="sm" iconOnly label="Página anterior" icon={<ChevronLeft />} disabled={page === 0} onClick={() => goPage(page - 1)} />
          <Button size="sm" iconOnly label="Próxima página" icon={<ChevronRight />} disabled={!hasNext} onClick={() => goPage(page + 1)} />
        </div>
      ) : null}
    </div>
  );
}
